/**
 * Price History Modal Component
 *
 * Modal to display price history for a catalog item.
 * @see US-8.10 - Modal Historique Prix
 */

import { TrendingUp, TrendingDown, Minus, History } from 'lucide-react';
import { Modal, Skeleton, Badge, EmptyState } from '@/components/ui';
import { usePriceHistory } from '../hooks/useCatalog';
import { useCurrency, useFormatDate } from '@/hooks';
import type { SupplierCatalogItemWithDetails } from '@bstock/shared';
import { formatCentimes, formatQuantity } from '@/lib/format';

interface PriceHistoryModalProps {
  supplierId: string;
  item: SupplierCatalogItemWithDetails;
  isOpen: boolean;
  onClose: () => void;
}

export function PriceHistoryModal({ supplierId, item, isOpen, onClose }: PriceHistoryModalProps) {
  const currency = useCurrency();
  const { formatDateShort } = useFormatDate();
  const { data, isLoading } = usePriceHistory(supplierId, item.id);

  const history = data?.history ?? [];
  const evolutionPercent = data?.evolutionPercent;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Historique des prix"
      size="md"
    >
      <div className="space-y-4">
        {/* Item Info */}
        <div className="bg-neutral-50 rounded-lg p-3">
          <p className="font-medium text-neutral-900">{item.rawProduct.name}</p>
          <p className="text-sm text-neutral-600">
            {item.packaging.name} ({formatQuantity(item.packaging.quantity)} {item.packaging.unit.symbol})
          </p>
        </div>

        {/* Evolution */}
        {evolutionPercent !== null && evolutionPercent !== undefined && history.length > 1 && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-neutral-50">
            {evolutionPercent > 0 ? (
              <>
                <TrendingUp className="w-5 h-5 text-danger-500" />
                <span className="text-danger-600 font-medium">+{evolutionPercent.toFixed(1)}%</span>
              </>
            ) : evolutionPercent < 0 ? (
              <>
                <TrendingDown className="w-5 h-5 text-success-500" />
                <span className="text-success-600 font-medium">{evolutionPercent.toFixed(1)}%</span>
              </>
            ) : (
              <>
                <Minus className="w-5 h-5 text-neutral-400" />
                <span className="text-neutral-600">Stable</span>
              </>
            )}
            <span className="text-sm text-neutral-500">depuis le premier prix enregistre</span>
          </div>
        )}

        {/* History List */}
        {isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-16" />
            ))}
          </div>
        ) : history.length === 0 ? (
          <EmptyState
            icon={History}
            title="Aucun historique de prix"
            description="L'historique des prix sera disponible apres la premiere modification"
            size="sm"
          />
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {history.map((price, index) => {
              const isCurrentPrice = !price.validTo;
              const previousPrice = history[index + 1];
              const priceChange = previousPrice && previousPrice.priceExclTax > 0
                ? ((price.priceExclTax - previousPrice.priceExclTax) / previousPrice.priceExclTax) * 100
                : null;

              // Calculate display unit price for this historical price
              let historyDisplayPrice: number | null = null;
              if (item.conversionToRecipeUnit > 0 && item.displayUnitSymbol) {
                // Calculate unit price ratio from current item
                const currentUnitPrice = item.unitPrice ?? 0;
                const currentPriceExclTax = item.currentPrice?.priceExclTax ?? 1;
                if (currentPriceExclTax > 0 && currentUnitPrice > 0) {
                  // Apply same ratio to historical price
                  const ratio = (item.displayUnitPrice ?? 0) / currentPriceExclTax;
                  historyDisplayPrice = price.priceExclTax * ratio;
                }
              }

              return (
                <div
                  key={price.id}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    isCurrentPrice
                      ? 'bg-primary-50 border-primary-200'
                      : 'bg-white border-neutral-200'
                  }`}
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-lg">
                        {formatCentimes(price.priceExclTax, currency)}
                      </span>
                      {isCurrentPrice && (
                        <Badge variant="primary">Actuel</Badge>
                      )}
                      {priceChange !== null && priceChange !== 0 && (
                        <Badge variant={priceChange > 0 ? 'danger' : 'success'}>
                          {priceChange > 0 ? '+' : ''}
                          {priceChange.toFixed(1)}%
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-neutral-500">
                      Du {formatDateShort(price.validFrom)}
                      {price.validTo ? ` au ${formatDateShort(price.validTo)}` : ' (en cours)'}
                    </p>
                  </div>
                  <div className="text-right text-sm text-neutral-500">
                    {historyDisplayPrice !== null && item.displayUnitSymbol
                      ? `${formatCentimes(historyDisplayPrice, currency)}/${item.displayUnitSymbol}`
                      : '-'}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
}
